import { ResultCounter } from './ResultCounter';
import { ResetButton } from './ResetButton';
import { ChangeViewTypeButton } from './ChangeViewTypeButton';
import React, { memo, RefObject, useMemo, useRef } from 'react';
import {
  AreaFilter,
  CategoryFilter,
  PersonFilter,
  SearchFilter,
  SearchFilterPopup,
  SortFilter,
  TimeFilter,
} from './Fields';
import { ChangeProductTypeButton } from './ChangeProductTypeButton';
import PopUpPage from 'Components/PopUpPage';
import { Portal } from 'react-portal';
import {
  actions as navActions,
  makeSelectNavIsActive,
  NavTypes,
} from 'core/redux/features/shared/nav';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, selectDeviceType } from 'core/redux/reducers';
import { useTrans } from 'core/hooks';
import { selectCountFilteredProducts } from 'core/redux/features/products/reducers/products';
import { Route } from 'react-router-dom';

export const FilterHeaderContainer = ({
  parentRef,
}: {
  parentRef: RefObject<HTMLElement>;
}) => {
  return (
    <div className="columns is-variable is-1">
      <div className="column">
        <div className="is-relative">
          <ResultCounter className="is-flex-mid-left" />
          <div className="is-hidden-tablet is-float-right">
            <SortFilter parentRef={parentRef} />
          </div>
        </div>
      </div>
      <div className="column">
        <div className="columns is-variable is-1 is-hidden-mobile">
          <div className="column">
            <ResetButton />
          </div>
          <div className="column">
            <ChangeViewTypeButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export const FilterFields = ({
  parentRef,
}: {
  parentRef: RefObject<HTMLElement>;
}) => {
  return (
    <>
      <div className="columns is-variable is-1">
        <div className="column">
          <TimeFilter />
        </div>
        <div className="column">
          <PersonFilter parentRef={parentRef} />
        </div>
        <div className="column">
          <AreaFilter parentRef={parentRef} />
        </div>
      </div>
      <div className="spacer-s" />
      <div className="columns is-variable is-1">
        <CategoryFilter parentRef={parentRef} className="column" />
        <div className="column">
          <SearchFilter />
        </div>
      </div>
    </>
  );
};

export const FiltersContainer = memo(() => {
  const filterRef: RefObject<HTMLDivElement> = useRef();
  return (
    <>
      <Route>
        {route => {
          const isMatch = route.location.hash.indexOf('search') > -1;
          if (!isMatch) {
            return null;
          }
          return <SearchFilterPopup />;
        }}
      </Route>
      <div id="product-filter-form" className="pane">
        <div className="is-flex">
          <div className="filters__item filters__item--left">
            <FilterHeaderContainer parentRef={filterRef} />
            <div className="spacer is-hidden-mobile" />
            <div className="filters" ref={filterRef}>
              <FilterFields parentRef={filterRef} />
            </div>
          </div>
          <div className="filters__item filters__item--right">
            <SortFilter parentRef={filterRef} />
          </div>
        </div>
      </div>
    </>
  );
});

export const FilterPopupContainer = () => {
  const selectActiveNav = useMemo(() => makeSelectNavIsActive(), []);
  const isActive = useSelector((state: AppState) =>
    selectActiveNav(state, NavTypes.Filter),
  );
  const dispatch = useDispatch();
  const trans = useTrans();
  const totals = useSelector(selectCountFilteredProducts);
  const deviceType = useSelector(selectDeviceType);

  return (
    <>
      <Portal node={document.body}>
        <PopUpPage
          id="pop-product-filters"
          hide={!isActive || deviceType !== 'mobile'}
          autoDisableScroll={true}
          whiteBackdrop={true}
        >
          <FiltersContainer />
          <button
            className="button is-red-dark filter-buttons--float"
            onClick={e => {
              e.preventDefault();
              dispatch(navActions.close(NavTypes.Filter));
            }}
          >
            {trans('save_search', { total: totals[0] })}
          </button>
        </PopUpPage>
      </Portal>
    </>
  );
};
